import { Suspense } from "react";
import { useParams } from "react-router";
import { useDomainBreadcrumbs } from "src/common/layout/useDomainBreadcrumbs.js";
import { useUser } from "src/common/context/userContextUtils.js";
import { COMPANYADMIN } from "src/common/service/users.js";
import { useDocumentTitle } from "src/common/utils/hooks/useDocumentTitle.js";
import { lazy } from "src/common/utils/lazy.js";
import { useHasExcelReportAccess } from "src/features/excelReports/hooks/useHasExcelReportAccess.js";
import { useGetPowerBiConnectionStatusQuery } from "src/features/powerBiReports/api/powerBiReportsApi.js";
import { useDevToolsUnlocked } from "src/features/sidebar/useDevToolsUnlock.js";
import {
	useLoadedCompanyDomain,
	useLoadedLegalEntities,
} from "src/routes/routeDataHooks.js";
import { DashboardHeader } from "./DashboardHeader.js";
import { StartSearch } from "./StartSearch.js";
import {
	DASHBOARD_WIDGETS,
	revenueByCompanyVisible,
} from "./widgets/dashboardWidgets.js";

// Code-split: bundle only loads once rendered, so it never delays the rest of the dashboard.
const { RevenueByCompanyWidget } = lazy(
	() => import("./widgets/revenueByCompany/RevenueByCompanyWidget.js"),
);

export const StartDashboard = () => {
	useDocumentTitle("Start");
	const breadcrumbs = useDomainBreadcrumbs();
	const { userIsSuperAdmin, hasPermissions } = useUser();
	const companyDomain = useLoadedCompanyDomain();
	const legalEntities = useLoadedLegalEntities();
	// Dev-tools gated: revenue-by-company is unreleased for everyone else.
	const devToolsUnlocked = useDevToolsUnlocked(userIsSuperAdmin());
	const hasExcelReportAccess = useHasExcelReportAccess(companyDomain.id);
	// WHY currentData: `data` keeps the previous domain's answer through a domain switch.
	const { currentData: powerBiConnection } = useGetPowerBiConnectionStatusQuery(
		companyDomain.id,
	);

	const { legalEntityId } = useParams();

	const widgetContext = {
		companyDomain,
		legalEntities,
		isSuperAdmin: userIsSuperAdmin(),
		devToolsUnlocked,
		hasExcelReportAccess,
		isCompanyAdmin: hasPermissions(companyDomain.id, [COMPANYADMIN]),
		powerBiConnected: powerBiConnection?.connected ?? false,
		legalEntityId,
	};
	const visibleWidgets = DASHBOARD_WIDGETS.filter(
		({ isVisible }) => isVisible?.(widgetContext) ?? true,
	);
	const showRevenueByCompany = revenueByCompanyVisible(widgetContext);

	return (
		<div className="min-h-full bg-grey-50 p-4 lg:p-6">
			<DashboardHeader breadcrumbs={breadcrumbs} search={<StartSearch />} />
			{/* WHY no filler for an odd count: the two grid tracks fix every widget's width on their own, so an empty last cell is just space. */}
			<div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
				{visibleWidgets.map(({ id, Component }) => (
					<Component key={id} />
				))}
				{showRevenueByCompany && (
					<Suspense fallback={null}>
						<RevenueByCompanyWidget />
					</Suspense>
				)}
			</div>
		</div>
	);
};
